import React, { useEffect, useState } from "react";

/**
 * Renders an email + mailto link only after client-side mount.
 * The raw email is never present in:
 *   - the initial HTML payload (server-rendered or view-source)
 *   - the JS bundle as a literal "user@domain.tld" string
 *
 * Scrapers that don't run JS (the vast majority) see nothing useful.
 * Scrapers that DO run JS can still find it, but the cost goes way up.
 *
 * The email is split + base64-encoded at module load and reassembled on mount.
 */

// Encoded in two chunks so the contact address never appears as a literal
// string anywhere in the bundle. Decoded at runtime, only after the component
// mounts in the browser.
const E_USER = "c3VwcG9ydA==";
const E_DOMAIN = "YWdlbnRyZXBseS5pbw==";

const decode = (b64) => {
  try {
    return typeof window !== "undefined" ? window.atob(b64) : "";
  } catch {
    return "";
  }
};

export function getContactEmail() {
  const u = decode(E_USER);
  const d = decode(E_DOMAIN);
  if (!u || !d) return "";
  return u + String.fromCharCode(64) + d; // "@" via charCode so the @ literal isn't searchable either
}

export default function ProtectedEmail({
  subject,
  body,
  display, // optional override of what is shown (defaults to "email us")
  className = "text-[#D95B38] hover:underline",
  testid = "protected-email",
}) {
  const [email, setEmail] = useState("");

  useEffect(() => {
    // Defer one tick so the email is only injected post-mount.
    const id = setTimeout(() => setEmail(getContactEmail()), 0);
    return () => clearTimeout(id);
  }, []);

  if (!email) {
    // Pre-hydration placeholder — no email is ever in the DOM yet
    return (
      <span data-testid={testid} className={className} aria-hidden="true">
        email&nbsp;us
      </span>
    );
  }

  const params = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  const href = `mailto:${email}${params.length ? "?" + params.join("&") : ""}`;

  return (
    <a href={href} data-testid={testid} className={className} rel="nofollow">
      {display || email}
    </a>
  );
}
